import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import { Field } from '@payaca/ambrosia';
import DeviceSearchFieldReadonly from './DeviceSearchFieldReadonly';

import Address from '@payaca/components/address/Address';

import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import { format } from 'date-fns';
import { get as _get } from 'lodash-es';
import { FC } from 'react';
import DiscriminatorFieldReadOnly from './DiscriminatorFieldReadOnly';
import ProjectFileSelectionReadonly from './ProjectFileSelectionReadonly';
import { getFieldLabelFromFieldSchema } from './utils/display';

export type Props = {
  name: string;
  fieldSchema: any;
  namespace?: string;
  definitions: Record<string, any>;
  formData: any;
};

export const DynamicViewField: FC<Props> = ({
  name,
  namespace,
  fieldSchema,
  definitions,
  formData,
}) => {
  /**
   * Queries
   */
  const { data: accountRegionPreferences } = useGetMyRegionPreferences();

  const helperText = fieldSchema.json_schema_extra.tooltip;
  const fieldLabel = getFieldLabelFromFieldSchema(fieldSchema);

  switch (fieldSchema.json_schema_extra.type) {
    case 'USER ACCOUNT DERIVED INPUT':
    case 'TEXT INPUT':
    case 'SELECT INPUT':
    case 'MPAN RECOGNISED INPUT': {
      return (
        <Field.Legacy>
          <Field.Label>{fieldLabel}</Field.Label>
          <Field.HelperText>{helperText}</Field.HelperText>

          <p>{_get(formData, name)}</p>
        </Field.Legacy>
      );
    }
    case 'RADIO INPUT':
      return (
        <Field.Legacy>
          <Field.Label>{fieldLabel}</Field.Label>
          <Field.HelperText>{helperText}</Field.HelperText>

          <p>
            {
              Object.entries<any>(fieldSchema.json_schema_extra.options).find(
                ([key, value]) => {
                  return value === _get(formData, name);
                }
              )?.[0]
            }
          </p>
        </Field.Legacy>
      );
    case 'DATE INPUT': {
      const value = _get(formData, name);

      return (
        <Field.Legacy>
          <Field.Label>{fieldLabel}</Field.Label>

          <Field.HelperText>{helperText}</Field.HelperText>

          <p>
            {value
              ? format(
                  value,
                  getInternationalDateFormatByLocale(
                    DateFormats.SHORT,
                    accountRegionPreferences?.locale
                  )
                )
              : 'Not set'}
          </p>
        </Field.Legacy>
      );
    }
    case 'FIXED INPUT AS TITLE':
      return <h2>{_get(formData, name)}</h2>;
    case 'ADDRESS RECOGNISED INPUT': {
      return (
        <Field.Legacy>
          <Field.Label>{fieldLabel}</Field.Label>
          <Field.HelperText>{helperText}</Field.HelperText>

          <p>
            <Address address={_get(formData, name)} />
          </p>
        </Field.Legacy>
      );
    }
    case 'SEARCH DEVICE RECOGNISED INPUT': {
      const deviceType = _get(formData, `${namespace}.deviceType`);
      return (
        <DeviceSearchFieldReadonly
          fieldSchema={fieldSchema}
          name={name}
          deviceType={deviceType}
          formData={formData}
        />
      );
    }
    case 'G100_SEARCH_RECOGNISED_INPUT_01': {
      return (
        <DeviceSearchFieldReadonly
          fieldSchema={fieldSchema}
          name={name}
          deviceType="G100_DEVICE"
          formData={formData}
        />
      );
    }
    case 'NESTED INPUT FORM DISCRIMINATED UNION 01': {
      // this should show a select field which on selection would conditionally show further schema
      return (
        <DiscriminatorFieldReadOnly
          fieldSchema={fieldSchema}
          name={name}
          namespace={namespace}
          definitions={definitions}
          formData={formData}
        />
      );
    }
    case 'SINGLE ATTACHMENT INPUT 01': {
      return (
        <Field.Legacy>
          <Field.Label>{fieldLabel}</Field.Label>
          <Field.HelperText>{helperText}</Field.HelperText>
          <ProjectFileSelectionReadonly
            name={name}
            formSection={fieldSchema}
            formData={formData}
          />
        </Field.Legacy>
      );
    }

    default:
      return null;
  }
};
